import { Link } from '@tanstack/react-router';
import { ChartLine, Share2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useStore } from '@/shared/store/store-context';

import { AnalyticsRangePicker } from '../components/analytics-range-picker';
import { ClicksSpendChart } from '../components/clicks-spend-chart';
import { SocialStatTile } from '../components/social-stat-tile';
import { TopProductsTable } from '../components/top-products-table';
import { useDayFormat } from '../hooks/use-day-format';
import { useSocialAnalytics } from '../hooks/use-social-analytics';
import {
  resolveAnalyticsRange,
  todayIn,
  type AnalyticsRangeDays,
} from '../schemas/analytics-range';

export interface SocialAnalyticsPageProps {
  /** From the URL; absent means the default range. */
  range?: AnalyticsRangeDays | undefined;
  onRangeChange: (days: AnalyticsRangeDays) => void;
}

function Analytics({ range: days, onRangeChange }: SocialAnalyticsPageProps) {
  const { t } = useTranslation('social');
  const store = useStore();
  const format = useFormat();
  const dayFormat = useDayFormat();
  const today = todayIn(store.timezone);
  const range = useMemo(() => resolveAnalyticsRange({ days }, today), [days, today]);
  const query = useSocialAnalytics(store.id, range);
  const data = query.data;

  const header = (
    <PageHeader
      title={t('analytics.title')}
      description={t('analytics.description', { store: store.name })}
    />
  );

  if (query.isError) {
    return (
      <PageContent>
        {header}
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </PageContent>
    );
  }

  if (data && !data.connected) {
    return (
      <PageContent>
        {header}
        <EmptyState
          icon={Share2}
          title={t('analytics.empty.notConnected.title')}
          description={t('analytics.empty.notConnected.description')}
          action={
            <Button asChild>
              <Link to="/s/$storeId/social/pinterest/connection" params={{ storeId: store.id }}>
                {t('analytics.empty.notConnected.action')}
              </Link>
            </Button>
          }
        />
      </PageContent>
    );
  }

  if (data && !data.hasData) {
    return (
      <PageContent>
        {header}
        <EmptyState
          icon={ChartLine}
          title={t('analytics.empty.noData.title')}
          description={t('analytics.empty.noData.description')}
        />
      </PageContent>
    );
  }

  const loading = data === undefined;
  const tiles = data?.tiles;
  const currency = tiles?.revenue.currency ?? store.defaultCurrency;
  const showSpend = tiles ? tiles.adSpend !== null : false;
  const money = (amount: number) => format.money({ amount, currency });

  return (
    <PageContent>
      {header}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <AnalyticsRangePicker range={range} onChange={onRangeChange} />
          {data ? (
            <p className="text-muted-foreground text-xs">
              {t('analytics.comparedWith', {
                range: dayFormat.range(data.previousFrom, data.previousTo),
                to: dayFormat.medium(data.to),
              })}
            </p>
          ) : null}
        </div>

        <div
          className={cn('grid grid-cols-2 gap-4', showSpend ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}
          data-tour="social.analytics.tiles"
        >
          <SocialStatTile
            label={t('analytics.tiles.impressions')}
            value={tiles ? format.number(tiles.impressions.value) : '—'}
            current={tiles?.impressions.value ?? 0}
            previous={tiles?.impressions.previous ?? 0}
            isLoading={loading}
          />
          <SocialStatTile
            label={t('analytics.tiles.outboundClicks')}
            value={tiles ? format.number(tiles.outboundClicks.value) : '—'}
            current={tiles?.outboundClicks.value ?? 0}
            previous={tiles?.outboundClicks.previous ?? 0}
            isLoading={loading}
          />
          {tiles?.adSpend ? (
            <SocialStatTile
              label={t('analytics.tiles.adSpend')}
              value={money(tiles.adSpend.amount)}
              current={tiles.adSpend.amount}
              previous={tiles.adSpend.previousAmount}
              neutral
            />
          ) : null}
          <SocialStatTile
            label={t('analytics.tiles.revenue')}
            value={tiles ? money(tiles.revenue.amount) : '—'}
            current={tiles?.revenue.amount ?? 0}
            previous={tiles?.revenue.previousAmount ?? 0}
            breakdown={
              tiles
                ? t('analytics.tiles.revenueBreakdown', {
                    organic: money(tiles.revenue.organicAmount),
                    paid: money(tiles.revenue.paidAmount),
                  })
                : undefined
            }
            isLoading={loading}
          />
        </div>

        <Card data-tour="social.analytics.chart">
          <CardHeader>
            <CardTitle className="text-[15px]">
              {showSpend ? t('analytics.chart.title') : t('analytics.chart.titleClicks')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data && tiles ? (
              <ClicksSpendChart
                series={data.series}
                adsConnected={data.adsConnected}
                currency={currency}
                summary={
                  tiles.adSpend
                    ? t('analytics.chart.summaryWithSpend', {
                        range: dayFormat.range(data.from, data.to),
                        clicks: format.number(tiles.outboundClicks.value),
                        spend: money(tiles.adSpend.amount),
                      })
                    : t('analytics.chart.summary', {
                        range: dayFormat.range(data.from, data.to),
                        clicks: format.number(tiles.outboundClicks.value),
                      })
                }
              />
            ) : (
              <Skeleton className="h-[280px] w-full" />
            )}
          </CardContent>
        </Card>

        <TopProductsTable items={data?.topProducts} isLoading={loading} />
      </div>
    </PageContent>
  );
}

export function SocialAnalyticsPage(props: SocialAnalyticsPageProps) {
  return (
    <FeatureErrorBoundary name="social.analytics">
      <Analytics {...props} />
    </FeatureErrorBoundary>
  );
}
